import React, { useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import blogImage from "../assets/images/blogs/blog.svg";
import blogImage2 from "../assets/images/blogs/blog2.avif";
import blogImage3 from "../assets/images/blogs/blog3.avif";
import blogImage4 from "../assets/images/blogs/blog4.avif";
import { useTranslation } from "react-i18next";
import { getBlogs } from "../redux/blogSlice";

const IMAGES = [blogImage, blogImage2, blogImage3, blogImage4];
const CARD_WIDTH = 300;
const GAP = 16;
const ANIM_DURATION = 80;

const NewsAndBlog = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { blogs } = useSelector((state) => state.blogs);

  const trackRef = useRef(null);
  const drag = useRef({ active: false, startX: 0, startTranslate: 0, moved: false });

  useEffect(() => {
    if (blogs.length === 0) {
      dispatch(getBlogs({ limit: 20, status: "published" }));
    }
  }, [dispatch, blogs.length]);

  const allBlogs = blogs.map((blog, index) => {
    const excerpt =
      blog.metaDescription ||
      blog.description?.replace(/<[^>]*>/g, "").slice(0, 120) ||
      blog.content?.replace(/<[^>]*>/g, "").slice(0, 120) ||
      "";
    return {
      id: blog._id || blog.id,
      title: blog.title,
      description: excerpt,
      link: `/blog/${blog._id || blog.id}`,
      image: blog.image || IMAGES[index % 4],
    };
  });

  const doubled = [...allBlogs, ...allBlogs];
  const loopWidth = allBlogs.length * (CARD_WIDTH + GAP);

  const getComputedTranslateX = useCallback(() => {
    const el = trackRef.current;
    if (!el) return 0;
    const transform = window.getComputedStyle(el).transform;
    if (!transform || transform === "none") return 0;
    return new DOMMatrix(transform).m41;
  }, []);

  const resumeFromX = useCallback((x) => {
    const el = trackRef.current;
    if (!el) return;
    let normalized = x % -loopWidth;
    if (normalized > 0) normalized -= loopWidth;
    const progress = Math.abs(normalized) / loopWidth;
    const delay = -(progress * ANIM_DURATION);
    el.style.transform = "";
    el.style.animation = `blog-scroll ${ANIM_DURATION}s linear ${delay}s infinite`;
  }, [loopWidth]);

  const onDragStart = useCallback((clientX) => {
    const el = trackRef.current;
    if (!el) return;
    const x = getComputedTranslateX();
    el.style.animation = "none";
    el.style.transform = `translateX(${x}px)`;
    drag.current = { active: true, startX: clientX, startTranslate: x, moved: false };
  }, [getComputedTranslateX]);

  const onDragMove = useCallback((clientX) => {
    if (!drag.current.active) return;
    const el = trackRef.current;
    if (!el) return;
    const delta = clientX - drag.current.startX;
    if (Math.abs(delta) > 3) drag.current.moved = true;
    let newX = drag.current.startTranslate + delta;
    if (newX > 0) newX -= loopWidth;
    if (newX < -loopWidth * 2) newX += loopWidth;
    el.style.transform = `translateX(${newX}px)`;
  }, [loopWidth]);

  const onDragEnd = useCallback(() => {
    if (!drag.current.active) return;
    drag.current.active = false;
    const el = trackRef.current;
    if (!el) return;
    const match = el.style.transform.match(/translateX\((.+)px\)/);
    const x = match ? parseFloat(match[1]) : 0;
    resumeFromX(x);
  }, [resumeFromX]);

  useEffect(() => {
    const onMouseMove = (e) => onDragMove(e.clientX);
    const onMouseUp = () => onDragEnd();
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onDragMove, onDragEnd]);

  // Touch events (passive: false needed for preventDefault)
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let startY = 0;
    const handleTouchStart = (e) => {
      startY = e.touches[0].clientY;
      onDragStart(e.touches[0].clientX);
    };
    const handleTouchMove = (e) => {
      const deltaX = Math.abs(e.touches[0].clientX - drag.current.startX);
      const deltaY = Math.abs(e.touches[0].clientY - startY);
      if (deltaX > deltaY) e.preventDefault();
      onDragMove(e.touches[0].clientX);
    };
    const handleTouchEnd = () => onDragEnd();
    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd);
    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [onDragStart, onDragMove, onDragEnd]);

  return (
    <div className="w-full text-center py-12 bg-white">
      <h2 className="font-roboto text-[28px] md:text-[32px] text-text mb-1">
        {t("newsAndBlogs")}
      </h2>
      <p className="font-poppins text-[16px] md:text-[18px] italic text-text mb-8">
        {t("blogText")}
      </p>

      <div className="relative overflow-hidden">
        <div
          className="absolute left-0 top-0 bottom-0 w-16 sm:w-28 z-10 pointer-events-none"
          style={{ background: "linear-gradient(to right, #ffffff, transparent)" }}
        />
        <div
          className="absolute right-0 top-0 bottom-0 w-16 sm:w-28 z-10 pointer-events-none"
          style={{ background: "linear-gradient(to left, #ffffff, transparent)" }}
        />

        <style>{`
          @keyframes blog-scroll {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-${loopWidth}px); }
          }
          .blog-track {
            animation: blog-scroll ${ANIM_DURATION}s linear infinite;
            cursor: grab;
            user-select: none;
          }
          .blog-track:active {
            cursor: grabbing;
          }
        `}</style>

        <div
          ref={trackRef}
          className="blog-track flex"
          style={{
            width: `${doubled.length * (CARD_WIDTH + GAP)}px`,
            gap: `${GAP}px`,
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            onDragStart(e.clientX);
          }}
        >
          {doubled.map((blog, index) => (
            <div
              key={`${blog.id}-${index}`}
              className="flex-shrink-0"
              style={{ width: `${CARD_WIDTH}px` }}
            >
              <div className="border border-cards-border rounded-[30px] bg-white shadow-sm h-[420px] flex flex-col overflow-hidden text-left">
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="w-full h-[160px] object-cover"
                  draggable={false}
                  onError={(e) => { e.target.src = IMAGES[0]; }}
                />
                <div className="flex flex-col flex-1 justify-between p-5">
                  <div>
                    <h3 className="font-poppins text-[16px] font-semibold text-text mb-2 line-clamp-2">
                      {blog.title}
                    </h3>
                    {blog.description && (
                      <p className="font-poppins text-gray-500 text-[13px] italic line-clamp-3 leading-relaxed">
                        {blog.description}
                      </p>
                    )}
                  </div>
                  <Link
                    to={blog.link}
                    className="font-poppins text-[13px] text-primary hover:underline mt-3 inline-block"
                    onClick={(e) => {
                      if (drag.current.moved) e.preventDefault();
                    }}
                  >
                    {t("more")}... →
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewsAndBlog;
